import React from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { axiosget } from "../../helpers/httprequest";
import PageTitle from "../../components/PageTitle";
import { Skeleton } from "../../components/ui/Skeleton";
import { Alert, AlertDescription } from "../../components/ui/Alert";
import { Card } from "../../components/ui/Card";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import EmptyState from "../../components/ui/EmptyState";
import { motion } from "framer-motion";
import { FiArrowLeft, FiFileText, FiDownload, FiExternalLink } from "react-icons/fi";

const Resources = () => {
  const { id } = useParams();

  const { data, isLoading, error } = useQuery({
    queryKey: ["courseMaterials", id],
    queryFn: async () => {
      const req = await axiosget(`ms_course_materials/${id}`);
      if (req?.type === "error") throw new Error(req?.message);
      return req?.data;
    },
  });

  const hasError = error || data?.type !== undefined;
  const errorMessage = error?.message || data?.message;
  const materials = !hasError && Array.isArray(data) ? data : [];

  return (
    <div>
      <div className="flex items-center gap-3 mb-2">
        <Link to="/app/materials">
          <Button variant="ghost" size="sm" leftIcon={<FiArrowLeft className="w-4 h-4" />}>
            Back to Materials
          </Button>
        </Link>
      </div>

      <PageTitle title="Course Materials" sub="Download and view lecture materials for this course" />

      {isLoading && (
        <div className="space-y-4 mt-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-20 rounded-xl" />)}
        </div>
      )}

      {hasError && (
        <Alert variant="danger" className="mt-4">
          <AlertDescription>{errorMessage || "Failed to load materials"}</AlertDescription>
        </Alert>
      )}

      {!isLoading && !hasError && materials.length === 0 && (
        <EmptyState
          variant="no-data"
          title="No Materials Available"
          description="No lecture materials have been uploaded for this course yet. Check back later."
          icon={<FiFileText className="h-12 w-12" />}
        />
      )}

      {materials.length > 0 && (
        <div className="space-y-3 mt-4">
          {materials.map((material, i) => (
            <motion.div
              key={material._id || i}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
            >
              <Card padding="none" className="hover:shadow-md transition-shadow">
                <div className="flex items-center gap-4 p-5">
                  <div className="w-12 h-12 rounded-xl bg-primary-50 flex items-center justify-center flex-shrink-0 border border-primary-100">
                    <FiFileText className="w-6 h-6 text-primary-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-bold text-neutral-900 truncate">
                      {material.topic?.title || material.title || "Untitled Material"}
                    </h3>
                    <p className="text-xs text-neutral-500 mt-0.5 truncate">
                      {material.topic?.description || material.description || "No description"}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {material.file && (
                      <a href={material.file} target="_blank" rel="noopener noreferrer">
                        <Button variant="outline" size="sm" leftIcon={<FiDownload className="w-3.5 h-3.5" />}>
                          Download
                        </Button>
                      </a>
                    )}
                    {material.link && (
                      <a href={material.link} target="_blank" rel="noopener noreferrer">
                        <Button variant="ghost" size="sm" leftIcon={<FiExternalLink className="w-3.5 h-3.5" />}>
                          Open
                        </Button>
                      </a>
                    )}
                  </div>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Resources;
